import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { FamilyCondition } from './FamilyCondition';
import { Customer } from './Customer';
import { FamilyHistory as GQLFamilyHistory } from '@hit/hit-api-client';

@supertypeClass({ toClient: true, toServer: true })
export class FamilyHistory extends Everything implements GQLFamilyHistory {
  @property({
    rule: ['required'],
    values: ['isalive', 'deceased', 'unknown'],
    descriptions: { isalive: '__ years old', deceased: 'Deceased', unknown: 'Unknown' },
    comment: 'Mother is still alive or deceased',
  })
  motherLiving: string;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(0, 100)', comment: 'Age mother died at' })
  motherDiedAt: number;

  @property({
    rule: ['required'],
    values: ['BreastorOvarianCancer', 'SkinCancer', 'OtherCancers', 'HeartDisease', 'KidneyDisease', 'Other'],
    descriptions: {
      BreastorOvarianCancer: 'Breast or Ovarian Cancer',
      SkinCancer: 'Skin Cancer',
      OtherCancers: 'Other Cancers',
      HeartDisease: 'Heart Disease',
      KidneyDisease: 'Kidney Disease',
      Other: 'Other',
    },
    comment: 'Disease type mothe died of',
  })
  motherDiedOf: string;

  @property({
    rule: ['required'],
    values: ['isalive', 'deceased', 'unknown'],
    descriptions: { isalive: '__ years old', deceased: 'Deceased', unknown: 'Unknown' },
    comment: 'Father is still alive or deceased',
  })
  fatherLiving: string;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(0, 100)', comment: 'Age father died at' })
  fatherDiedAt: number;

  @property({
    rule: ['required'],
    values: ['ProstateCancer', 'BreastCancer', 'SkinCancer', 'OtherCancers', 'HeartDisease', 'KidneyDisease', 'Other'],
    descriptions: {
      ProstateCancer: 'Prostate Cancer',
      BreastCancer: 'Breast Cancer',
      SkinCancer: 'Skin Cancer',
      OtherCancers: 'Other Cancers',
      HeartDisease: 'Heart Disease',
      KidneyDisease: 'Kidney Disease',
      Other: 'Other',
    },
    comment: 'Disease type father died of',
  })
  fatherDiedOf: string;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'Nobiologicalbrothers', 'Unknown'],
    descriptions: { Yes: 'Yes', No: 'No', Nobiologicalbrothers: 'No biological brothers', Unknown: 'Unknown' },
    comment: 'Are all biological brothers living',
  })
  brothersLiving: string;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'Nobiologicalsisters', 'Unknown'],
    descriptions: { Yes: 'Yes', No: 'No', Nobiologicalsisters: 'No biological sisters', Unknown: 'Unknown' },
    comment: 'Are all biological sisters living',
  })
  sistersLiving: string;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'Unknown'],
    descriptions: { Yes: 'Yes', No: 'No', Unknown: 'Unknown' },
    comment:
      'Have any of your immediate family members diagnosed with cancer, heart disease, diabetes, or kidney disease',
  })
  hasConditions: string;

  @property({ type: Array, getType: () => FamilyCondition })
  familyConditions: Array<FamilyCondition> = [];

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'Unknown'],
    descriptions: { Yes: 'Yes', No: 'No', Unknown: 'Unknown' },
    comment:
      "Have any of your immediate family members diagnosed with Polycystic Kidney Disease, Huntington's Disease, Marfan Syndrome, Lynch Syndrome, or Cardiomyopathy (Heart Disease)",
  })
  hadSeriousCondition: string;

  @property({
    comment: 'Details for serious condition',
  })
  hadSeriousConditionDetails: string;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'Unknown'],
    descriptions: { Yes: 'Yes', No: 'No', Unknown: 'Unknown' },
    comment:
      "To the best of your knowledge, have any of {{'noun': 'insured', 'type': 'possessive'}} immediate family members (biological parents, biological siblings) ever been diagnosed by a member of the medical profession with Familial Polyposis, Gardener Syndrome, HNPCC, ALS, Spinomuscular Atrophy, Multiple Endocrine Neoplasia, BRCA 1 or 2, Long QT syndrome, or Brugada syndrome?",
  })
  hadHeritableCondition: string;

  @property({
    comment: 'Details for heritable condition',
  })
  hadHeritableConditionDetails: string;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment:
      "My biological parents or biological siblings have been diagnosed by a member of the medical profession with any of the following: cancer, heart disease, diabetes, kidney disease, Huntington's Disease, Marfan Syndrome, or Lynch Syndrome.",
  })
  hadSeriousConditionAndOrCancer: string;

  @property({ rule: 'numeric', validate: 'isWithin(31, 110)', comment: "Mother's age" })
  motherAge: number;

  @property({ rule: 'numeric', validate: 'isWithin(31, 110)', comment: "Father's age" })
  fatherAge: number;

  @property({ getType: () => Customer })
  customer: Customer;

  constructor() {
    super();
  }

  static new(customer) {
    return new FamilyHistory().init(customer);
  }

  init(customer) {
    this.customer = customer;
    return this;
  }

  hasConditionsTrigger() {
    if (this.hasConditions === 'Yes') {
      if (this.familyConditions.length === 0) {
        this.familyConditions.push(FamilyCondition.new(this.customer));
      }
    } else {
      this.familyConditions = [];
    }
  }

  hadSeriousConditionAndOrCancerTrigger() {
    if (this.hadSeriousConditionAndOrCancer === 'Yes') {
      if (this.familyConditions.length === 0) {
        this.familyConditions.push(FamilyCondition.new(this.customer));
      }
    } else {
      this.familyConditions = [];
    }
  }

  motherLivingTrigger() {
    if (this.motherLiving !== 'isalive') {
      this.motherAge = null;
    }
  }

  fatherLivingTrigger() {
    if (this.fatherLiving !== 'isalive') {
      this.fatherAge = null;
    }
  }
}
